import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  ImageIcon,
  Upload,
  Link as LinkIcon,
  FolderOpen,
  Loader2,
  Trash2,
  Check,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { uploadFile, fetchGallery, fetchGalleryTable, deleteUpload } from "@/lib/api";

/**
 * 3-tab image picker: Upload (R2) · Paste URL · My gallery (R2 listing).
 * The component exposes the same simple API as before:
 *   <ImageUpload value={url} onChange={setUrl} shape="circle|square"
 *                userRecordId={user.user_record_id} kind="photo" />
 *
 * `userRecordId` is required for Upload and Gallery; URL paste works without it.
 */
export default function ImageUpload({
  label = "Image",
  value,
  onChange,
  shape = "circle",
  testId = "image-upload",
  userRecordId,
  kind = "photo",
  gallerySource = "uploads",
}) {
  const [open, setOpen] = useState(false);

  const isCircle = shape === "circle";
  const previewBox = isCircle ? "w-20 h-20 rounded-full" : "w-24 h-16 rounded-lg";

  function clear() {
    onChange?.("");
    toast.success(`${label} cleared`);
  }

  return (
    <div data-testid={testId}>
      <Label className="text-xs font-medium text-zinc-700 flex items-center gap-1.5">
        <ImageIcon size={12} className="text-zinc-400" /> {label}
      </Label>
      <div className="mt-2 flex items-start gap-3">
        <div
          className={`${previewBox} bg-zinc-100 overflow-hidden border border-zinc-200 flex items-center justify-center shrink-0`}
        >
          {value ? (
            <img
              src={value}
              alt={label}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <ImageIcon size={20} className="text-zinc-400" />
          )}
        </div>

        <div className="flex-1 min-w-0 space-y-2">
          {value ? (
            <div className="text-[11px] text-emerald-600 font-medium flex items-center gap-1" data-testid={`${testId}-set-label`}>
              <Check size={12} /> Image set
            </div>
          ) : (
            <div className="text-[11px] text-zinc-400 italic">
              No image set
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-9 rounded-lg border-zinc-200 text-sm"
                  data-testid={`${testId}-open-btn`}
                >
                  <ImageIcon size={13} className="mr-1.5" />
                  {value ? "Change" : "Add image"}
                </Button>
              </DialogTrigger>
              <PickerDialog
                onClose={() => setOpen(false)}
                onPick={(url) => {
                  onChange?.(url);
                  setOpen(false);
                  toast.success(`${label} updated`);
                }}
                userRecordId={userRecordId}
                kind={kind}
                gallerySource={gallerySource}
                testId={testId}
              />
            </Dialog>

            {value && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={clear}
                className="h-9 rounded-lg text-sm text-rose-600 hover:bg-rose-50"
                data-testid={`${testId}-clear-btn`}
              >
                <X size={13} className="mr-1" /> Clear
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Dialog with 3 tabs ---------- */
function PickerDialog({ onPick, onClose, userRecordId, kind, gallerySource, testId }) {
  return (
    <DialogContent
      className="max-w-md w-[95vw] sm:w-full p-0 overflow-hidden"
      data-testid={`${testId}-dialog`}
    >
      <DialogHeader className="px-5 pt-5">
        <DialogTitle className="text-lg font-semibold">
          Choose an image
        </DialogTitle>
      </DialogHeader>

      <Tabs defaultValue="upload" className="px-5 pb-5 pt-2">
        <TabsList className="grid grid-cols-3 mb-3">
          <TabsTrigger value="upload" data-testid={`${testId}-tab-upload`}>
            <Upload size={13} className="mr-1.5" /> Upload
          </TabsTrigger>
          <TabsTrigger value="url" data-testid={`${testId}-tab-url`}>
            <LinkIcon size={13} className="mr-1.5" /> URL
          </TabsTrigger>
          <TabsTrigger value="gallery" data-testid={`${testId}-tab-gallery`}>
            <FolderOpen size={13} className="mr-1.5" /> Gallery
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upload">
          <UploadTab onPick={onPick} userRecordId={userRecordId} kind={kind} testId={testId} />
        </TabsContent>
        <TabsContent value="url">
          <UrlTab onPick={onPick} testId={testId} />
        </TabsContent>
        <TabsContent value="gallery">
          <GalleryTab onPick={onPick} userRecordId={userRecordId} kind={kind} gallerySource={gallerySource} testId={testId} />
        </TabsContent>
      </Tabs>
    </DialogContent>
  );
}

/* ---------- Upload tab (multipart -> R2) ---------- */
function UploadTab({ onPick, userRecordId, kind, testId }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progressText, setProgressText] = useState("");

  async function handleFile(f) {
    if (!f) return;
    if (!userRecordId) {
      toast.error("Sign in to upload");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("File too large (max 5 MB)");
      return;
    }
    setUploading(true);
    setProgressText(`Uploading ${f.name}…`);
    try {
      const res = await uploadFile(f, userRecordId, kind);
      onPick(res.url);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Upload failed";
      toast.error(detail);
    } finally {
      setUploading(false);
      setProgressText("");
    }
  }

  return (
    <div data-testid={`${testId}-upload-tab`}>
      <div
        onClick={() => inputRef.current?.click()}
        className="cursor-pointer rounded-xl border-2 border-dashed border-zinc-300 hover:border-violet-400 hover:bg-violet-50/30 p-6 text-center transition-colors"
        data-testid={`${testId}-upload-zone`}
      >
        {uploading ? (
          <Loader2 className="mx-auto animate-spin text-violet-600 mb-1" />
        ) : (
          <Upload size={22} className="mx-auto text-zinc-400 mb-1" />
        )}
        <div className="text-sm font-medium text-zinc-900">
          {uploading ? progressText : "Click to upload"}
        </div>
        <div className="text-[11px] text-zinc-500 mt-0.5">
          PNG, JPG, WEBP, GIF · up to 5 MB · saved to your gallery
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          className="hidden"
          data-testid={`${testId}-file-input`}
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </div>
    </div>
  );
}

/* ---------- URL tab ---------- */
function UrlTab({ onPick, testId }) {
  const [url, setUrl] = useState("");
  const ok = url && /^https?:\/\//i.test(url);
  return (
    <div className="space-y-3" data-testid={`${testId}-url-tab`}>
      <Input
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://example.com/image.jpg"
        className="h-10 rounded-lg"
        data-testid={`${testId}-url-input`}
      />
      {ok && (
        <div className="rounded-xl border border-zinc-200 p-3 flex items-center gap-3">
          <img
            src={url}
            alt="preview"
            className="w-16 h-16 rounded-lg object-cover bg-zinc-100"
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
          <div className="text-xs text-zinc-500 truncate flex-1">{url}</div>
        </div>
      )}
      <Button
        type="button"
        size="sm"
        disabled={!ok}
        onClick={() => onPick(url.trim())}
        className="w-full h-10 rounded-lg bg-violet-600 hover:bg-violet-700"
        data-testid={`${testId}-url-use-btn`}
      >
        <Check size={14} className="mr-1.5" /> Use this URL
      </Button>
    </div>
  );
}

/* ---------- Gallery tab ---------- */
function GalleryTab({ onPick, userRecordId, kind, gallerySource, testId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(true);
  const [err, setErr] = useState(null);

  const reload = useCallback(async () => {
    if (!userRecordId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      const res = gallerySource === "airtable"
        ? await fetchGalleryTable(userRecordId)
        : await fetchGallery(userRecordId);
      setConfigured(res?.configured !== false);
      setItems(res?.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Could not load gallery");
    } finally {
      setLoading(false);
    }
  }, [userRecordId, kind, gallerySource]);

  useEffect(() => {
    let active = true;
    (async () => {
      await reload();
      if (!active) return;
    })();
    return () => {
      active = false;
    };
  }, [reload]);

  async function handleDelete(key) {
    if (!confirm("Delete this image from your gallery?")) return;
    try {
      await deleteUpload(userRecordId, key);
      setItems((prev) => prev.filter((i) => i.key !== key));
      toast.success("Deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  }

  if (loading) {
    return (
      <div className="py-10 text-center text-zinc-500" data-testid={`${testId}-gallery-loading`}>
        <Loader2 className="mx-auto animate-spin mb-1" />
        <div className="text-xs">Loading gallery…</div>
      </div>
    );
  }
  if (!configured) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid={`${testId}-gallery-not-configured`}>
        Cloudflare R2 isn&apos;t configured yet. Add <code>R2_ACCESS_KEY_ID</code>,{" "}
        <code>R2_SECRET_ACCESS_KEY</code> and <code>R2_ENDPOINT_URL</code> to{" "}
        <code>backend/.env</code> to enable uploads & gallery.
      </div>
    );
  }
  if (err) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900" data-testid={`${testId}-gallery-error`}>
        {err}
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-zinc-300 p-8 text-center text-sm text-zinc-500" data-testid={`${testId}-gallery-empty`}>
        <FolderOpen size={20} className="mx-auto text-zinc-400 mb-1" />
        Your gallery is empty. Upload images on the <strong>Upload</strong> tab.
      </div>
    );
  }
  return (
    <div data-testid={`${testId}-gallery-tab`}>
      <div className="text-[11px] text-zinc-500 mb-2">
        {items.length} image{items.length === 1 ? "" : "s"} · click to use
      </div>
      <ul className="grid grid-cols-3 gap-2 max-h-72 overflow-y-auto pr-1">
        {items.map((it) => (
          <li
            key={it.key}
            className="group relative rounded-lg overflow-hidden border border-zinc-200 hover:border-violet-400 transition-colors aspect-square"
            data-testid={`${testId}-gallery-item`}
          >
            <button
              type="button"
              onClick={() => onPick(it.url)}
              className="absolute inset-0 w-full h-full bg-zinc-100"
            >
              <img
                src={it.url}
                alt={it.filename}
                className="w-full h-full object-cover"
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            </button>
            <button
              type="button"
              onClick={() => handleDelete(it.key)}
              className={`absolute top-1 right-1 w-6 h-6 rounded-full bg-white/95 hover:bg-rose-50 hover:text-rose-600 text-zinc-500 opacity-0 group-hover:opacity-100 transition-opacity items-center justify-center shadow ${gallerySource === "airtable" ? "hidden" : "flex"}`}
              data-testid={`${testId}-gallery-delete`}
              aria-label="Delete"
            >
              <Trash2 size={11} />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
